import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import {
  collection,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import '../styles/JoinTeam.css';

const STATUS_META = {
  open: { label: 'Approval required', color: 'amber', cta: 'Request to Join', disabled: false },
  request: { label: 'Approval required', color: 'amber', cta: 'Request to Join', disabled: false },
  private: { label: 'Private', color: 'red', cta: 'Invite required', disabled: true }
};

const validateIgn = (value) => {
  const trimmed = value.trim();
  if (trimmed.length < 3 || trimmed.length > 16) return 'IGN must be 3-16 characters.';
  if (!/^[a-zA-Z0-9 _.-]+$/.test(trimmed)) return 'Only letters, numbers, spaces, ._- allowed.';
  return '';
};

const CURRENT_YEAR = String(new Date().getFullYear());
const REGION_COLORS = {
  'india': '#FF9F1C',
  'africa': '#2ECC71',
  'north america': '#3A86FF',
  'china': '#E63946',
  'japan': '#FF5A5F',
  'middle east': '#6A4C93',
  'europe': '#00B4D8',
  'garena': '#00E5A8',
  'sea (garena)': '#00E5A8',
  'sea': '#00E5A8',
};

const regionStyle = (region) => {
  const key = (region || '').toLowerCase();
  const color = REGION_COLORS[key];
  if (!color) return {};
  return {
    backgroundColor: `${color}22`,
    color: '#fff',
    border: `1px solid ${color}55`
  };
};

const JoinTeam = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [teams, setTeams] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState('');
  const [ign, setIgn] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showIgnModal, setShowIgnModal] = useState(false);
  const [ignModalValue, setIgnModalValue] = useState('');
  const [ignModalError, setIgnModalError] = useState('');
  const [userRequests, setUserRequests] = useState({}); // Map of teamId -> request status
  const backTarget = location.state?.from || '/team-decision';

  // Only allow entry when navigated from approved pages
  useEffect(() => {
    const allowedSources = ['team-decision', 'settings'];
    const source = location.state?.source;
    if (!allowedSources.includes(source)) {
      navigate('/team-decision', { replace: true });
    }
  }, [location.state, navigate]);

  // Live updates: join request status + approval detection
  useEffect(() => {
    const user = auth?.currentUser;
    if (!user) return;

    const q = query(collection(db, 'joinRequests'), where('userId', '==', user.uid));
    const unsubscribe = onSnapshot(q, (snap) => {
      const requestsMap = {};
      snap.docs.forEach((docSnap) => {
        const data = docSnap.data();
        const teamId = data.teamId;
        if (!teamId) return;

        const rawStatus = data.status || 'pending';
        const status = rawStatus === 'denied' ? 'rejected' : rawStatus;

        requestsMap[teamId] = {
          status,
          requestId: docSnap.id,
          createdAt: data.createdAt
        };

      });

      setUserRequests(requestsMap);
    });

    return () => unsubscribe();
  }, []);

  // Redirect to main app once approved (teamId is set)
  useEffect(() => {
    const user = auth?.currentUser;
    if (!user) return;

    const userRef = doc(db, `users/${user.uid}`);
    const unsubscribe = onSnapshot(userRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      if (data?.teamId) {
        navigate('/my-strategy', { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const fetchDirectory = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'teamDirectory'));
        if (snap.empty) {
          setStatusMsg('No teams are visible yet. Ask your coach to publish the team.');
          setTeams([]);
          setFiltered([]);
          setSelectedId(null);
        } else {
          // Enrich each team with its season doc for CURRENT_YEAR
          const data = await Promise.all(
            snap.docs.map(async (d) => {
              const base = { id: d.id, ...d.data() };
              try {
                const seasonRef = doc(db, 'teams', d.id, 'seasons', CURRENT_YEAR);
                const seasonSnap = await getDoc(seasonRef);
                const seasonData = seasonSnap.exists() ? seasonSnap.data() : {};
                return {
                  ...base,
                  name: seasonData.displayName || base.name,
                  logo: seasonData.logo || base.logo,
                  region: seasonData.region || base.region,
                  season: seasonData.year || base.season || CURRENT_YEAR,
                };
              } catch (err) {
                console.error('Season fetch failed for', d.id, err);
                return base;
              }
            })
          );
          setTeams(data);
          setFiltered(data);
          setSelectedId(data[0]?.id || null);
        }
      } catch (err) {
        console.error('Failed to load team directory', err);
        setStatusMsg('Cannot load teams (permissions). Showing sample data.');
        const sample = [
          { id: 'alpha', name: 'Alpha Wolves', season: CURRENT_YEAR, league: 'Major', members: 9, capacity: 20, status: 'request', region: 'NA-East', description: 'Looking for AR + Support for Spring.' },
          { id: 'bravo', name: 'Bravo Squad', season: CURRENT_YEAR, league: 'Open Ladder', members: 4, capacity: 20, status: 'request', region: 'EU', description: 'Daily scrims. New players welcome.' },
          { id: 'charlie', name: 'Charlie Black', season: CURRENT_YEAR, league: 'Challengers', members: 20, capacity: 20, status: 'private', region: 'Asia', description: 'Roster locked; invite only.' },
        ];
        setTeams(sample);
        setFiltered(sample);
        setSelectedId(sample[0].id);
      } finally {
        setLoading(false);
      }
    };
    fetchDirectory();
  }, []);

  useEffect(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      setFiltered(teams);
      return;
    }
    setFiltered(
      teams.filter((t) =>
        [t.name, t.region, t.league, t.season]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(term))
      )
    );
  }, [search, teams]);

  const selectedTeam = useMemo(
    () => filtered.find((t) => t.id === selectedId) || filtered[0],
    [filtered, selectedId]
  );

  useEffect(() => {
    if (selectedTeam) setSelectedId(selectedTeam.id);
  }, [selectedTeam]);

  const meta = STATUS_META[selectedTeam?.status || 'request'];
  const rawCap = selectedTeam?.capacity || 7;
  const spotsCap = Math.min(rawCap, 7);
  const rawUsed = selectedTeam ? Math.min(selectedTeam.members || 0, rawCap) : 0;
  const spotsUsed = Math.min(rawUsed, spotsCap);
  const capacityPct = Math.min(100, Math.round((spotsUsed / spotsCap) * 100));
  const isFull = spotsUsed >= spotsCap;
  const visibilityLabel = meta.label;
  
  // Get request status for selected team
  const requestStatus = selectedTeam ? userRequests[selectedTeam.id] : null;
  const hasPendingRequest = requestStatus?.status === 'pending';
  const hasRejectedRequest = requestStatus?.status === 'rejected';
  const hasApprovedRequest = requestStatus?.status === 'approved';

  const submitRequest = async (ignToUse) => {
    if (!selectedTeam) return;

    const user = auth?.currentUser;
    if (!user) {
      setStatusMsg('You must be logged in.');
      return;
    }

    setSubmitting(true);
    setStatusMsg('');
    try {
      // Block duplicate pending requests for this team/user
      const pendingSnap = await getDocs(
        query(
          collection(db, 'joinRequests'),
          where('teamId', '==', selectedTeam.id),
          where('userId', '==', user.uid),
          where('status', '==', 'pending')
        )
      );
      if (!pendingSnap.empty) {
        setStatusMsg('You already have a pending request for this team.');
        setSubmitting(false);
        return;
      }

      await addDoc(collection(db, 'joinRequests'), {
        userId: user.uid,
        email: user.email || '',
        teamId: selectedTeam.id,
        teamName: selectedTeam.name,
        ign: ignToUse.trim(),
        message: message.trim(),
        status: 'pending',
        visibility: 'request',
        createdAt: serverTimestamp()
      });
      setStatusMsg('✅ Request sent! The coach will review your request and notify you when approved.');
      // Clear form after successful submission
      setIgn('');
      setMessage('');
      // Update user requests to show pending status
      setUserRequests(prev => ({
        ...prev,
        [selectedTeam.id]: { status: 'pending', requestId: 'new', createdAt: new Date() }
      }));
    } catch (err) {
      console.error('Failed to submit join request', err);
      setStatusMsg('❌ Could not submit request. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedTeam) return;
    if (meta.disabled) return;
    if (isFull) return;

    const user = auth?.currentUser;
    if (!user) {
      setStatusMsg('You must be logged in.');
      return;
    }

    // Check if IGN is required but missing - always show modal if IGN is empty
    const needsIgn = selectedTeam.status !== 'open';
    if (needsIgn && !ign.trim()) {
      console.log('Showing IGN modal - IGN is missing');
      setShowIgnModal(true);
      setIgnModalValue('');
      setIgnModalError('');
      return;
    }

    // Also check if IGN exists but is invalid
    if (ign.trim()) {
      const ignError = validateIgn(ign);
      if (ignError) {
        setStatusMsg(ignError);
        return;
      }
    }

    await submitRequest(ign);
  };

  const handleIgnModalSubmit = () => {
    const error = validateIgn(ignModalValue);
    if (error) {
      setIgnModalError(error);
      return;
    }
    setIgn(ignModalValue.trim());
    setShowIgnModal(false);
    setIgnModalValue('');
    setIgnModalError('');
    // Automatically submit after IGN is provided
    submitRequest(ignModalValue.trim());
  };

  return (
    <>
      {showIgnModal && (
        <div className="modal-overlay" onClick={() => setShowIgnModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enter Your In-Game Name</h3>
              <button className="modal-close" onClick={() => setShowIgnModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">We need your in-game name (IGN) to process your join request.</p>
              <label className="field">
                <span>In-game name (required)</span>
                <input
                  type="text"
                  value={ignModalValue}
                  onChange={(e) => {
                    setIgnModalValue(e.target.value);
                    if (ignModalError) setIgnModalError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleIgnModalSubmit();
                  }}
                  placeholder="e.g., NW_Player"
                  autoFocus
                />
                {ignModalError && <p className="field-error">{ignModalError}</p>}
              </label>
            </div>
            <div className="modal-footer">
              <button className="ghost" onClick={() => setShowIgnModal(false)}>Cancel</button>
              <button className="primary" onClick={handleIgnModalSubmit}>Continue</button>
            </div>
          </div>
        </div>
      )}
    <div className="join-wrapper">
      <div className="join-card directory">
        <div className="topbar">
          <div className="topbar-left">
            <button className="back-link" onClick={() => navigate(backTarget, { replace: true })}>← | Join a Team</button>
            <span className="topbar-note"></span>
          </div>
          <div className="topbar-right">
            <input
              className="search"
              placeholder="Search team, region, league"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button className="ghost" onClick={() => navigate('/create-team')}>Create Team</button>
          </div>
        </div>

        <div className="layout">
          <div className="list-panel">
            <span className="topbar-note" style={{ display: 'block', marginBottom: '0.35rem' }}>
              Browse teams and see players and region at a glance.
            </span>
            <div className="list-head">
              <span>Team</span>
              <span>Players</span>
              <span>Region</span>
            </div>
            <div className="list-body">
              {loading && <div className="empty">Loading teams…</div>}
              {!loading && filtered.length === 0 && <div className="empty">No teams match your search.</div>}
              {!loading && filtered.map((team) => {
                const m = STATUS_META[team.status || 'request'];
                const capRaw = team.capacity || 7;
                const cap = Math.min(capRaw, 7);
                const used = Math.min(team.members || 0, cap);
                const pct = Math.min(100, Math.round((used / cap) * 100));
                const full = used >= cap;
                const selected = team.id === selectedId;
                const logo = team.logo;
                const teamRequestStatus = userRequests[team.id];
                return (
                  <button
                    key={team.id}
                    className={`row ${selected ? 'selected' : ''}`}
                    onClick={() => setSelectedId(team.id)}
                  >
                    <span className="cell team-cell">
                      <div className="logo-fallback">
                        {logo ? (
                          <img src={logo} alt={`${team.name || 'Team'} logo`} />
                        ) : (
                          team.name?.[0] || '?'
                        )}
                      </div>
                      <div>
                        <p className="team-name">
                          {team.name}
                          {teamRequestStatus && (
                            <span className={`team-status-indicator ${teamRequestStatus.status}`}>
                              {teamRequestStatus.status === 'pending' ? ' Pending' : teamRequestStatus.status === 'approved' ? ' Approved' : ' Rejected'}
                            </span>
                          )}
                        </p>
                        <p className="team-sub">{team.league || 'League'} · {team.season || 'Season'}</p>
                      </div>
                    </span>
                    <span className="cell players-cell">
                      <span className={`capacity ${full ? 'full' : ''}`}>{used} / {cap}</span>
                      <span className="cap-bar">
                        <span className="cap-fill" style={{ width: `${pct}%` }} />
                      </span>
                    </span>
                    <span className="cell region-cell">
                      <span className="region-pill" style={regionStyle(team.region)}>{team.region || 'Region'}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="detail-panel">
            {!selectedTeam && <div className="empty">Select a team to see details.</div>}
            {selectedTeam && (
              <>
                <div className="detail-head">
                  <div className="detail-logo">
                    {selectedTeam.logo ? (
                      <img src={selectedTeam.logo} alt={`${selectedTeam.name || 'Team'} logo`} />
                    ) : (
                      <span>{selectedTeam.name?.[0]}</span>
                    )}
                  </div>
                  <div>
                    <p className="detail-name">{selectedTeam.name}</p>
                    <p className="detail-sub">{selectedTeam.region || 'Region'} · {selectedTeam.league || 'League'} · {selectedTeam.season || 'Season'}</p>
                  </div>
                </div>

                <div className="info-block">
                  <div className="info-head">
                    <span>Team Info</span>
                    <span className={`pill ${meta.color}`}>{visibilityLabel}</span>
                  </div>
                  {requestStatus && (
                    <div className="request-status-banner">
                      <span
                        className={`status-badge ${
                          requestStatus.status === 'pending'
                            ? 'pending'
                            : requestStatus.status === 'approved'
                            ? 'approved'
                            : 'rejected'
                        }`}
                      >
                        {requestStatus.status === 'pending'
                          ? 'Pending'
                          : requestStatus.status === 'approved'
                          ? 'Approved'
                          : 'Rejected'}
                      </span>
                      <p className="status-text">
                        {requestStatus.status === 'pending'
                          ? 'Your request is pending review by the coach.'
                          : requestStatus.status === 'approved'
                          ? 'Approved! Redirecting you to the main app...'
                          : 'Your request was rejected. You can try again or contact the coach.'}
                      </p>
                    </div>
                  )}
                  <div className="info-grid">
                    <div>
                      <p className="muted">Players</p>
                      <p className="strong">{spotsUsed} / {spotsCap}</p>
                    </div>
                    <div>
                      <p className="muted">Region</p>
                      <p className="strong">{selectedTeam.region || 'Region'}</p>
                    </div>
                    <div>
                      <p className="muted">Visibility</p>
                      <p className="strong">Request-only</p>
                    </div>
                  </div>
                  <p className="info-subtext">Coach approval is required for all teams.</p>
                </div>

                {selectedTeam.status !== 'private' && (
                  <div className="detail-form">
                    {statusMsg && (
                      <div className={`status-banner ${statusMsg.includes('sent') || statusMsg.includes('✅') ? 'success' : 'error'}`}>
                        {statusMsg}
                      </div>
                    )}
                    {/* IGN field removed - now handled by modal when needed */}
                    <label className="field">
                      <span>Message (optional)</span>
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => {
                          setMessage(e.target.value);
                          if (statusMsg) setStatusMsg(''); // Clear status when user types
                        }}
                        placeholder={`Hey, I'm joining for the ${CURRENT_YEAR} season`}
                      />
                    </label>
                    <div className="detail-actions">
                      <button 
                        className="primary" 
                        disabled={submitting || meta.disabled || (isFull && meta.label === 'Open') || hasPendingRequest || hasApprovedRequest} 
                        onClick={handleSubmit}
                      >
                        {submitting ? 'Sending...' : hasPendingRequest ? 'Request Pending' : hasApprovedRequest ? 'Approved' : meta.cta}
                      </button>
                      <button className="ghost" onClick={() => {
                        setMessage('');
                        setStatusMsg('');
                      }} disabled={submitting}>Clear note</button>
                    </div>
                    {hasPendingRequest && <p className="status-message">You already have a pending request for this team.</p>}
                    {hasRejectedRequest && <p className="status-message">You have been rejected by this team. You can submit a new request.</p>}
                    {isFull && meta.label === 'Open' && !hasPendingRequest && <p className="status-message">Team is full. You can still request if admins allow more spots.</p>}
                  </div>
                )}
                {selectedTeam.status === 'private' && (
                  <p className="status-message">Invite required. Ask the captain for an invite.</p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default JoinTeam;
